import React, { FC, useState } from 'react';
import { FCOFields, FeatureViewFCO, FeatureViewFCOFields, FeatureViewFCOType } from '../../../core/types/fcoTypes';
import { MonitoringDateRange } from '../../monitoring/DateRange-Dropdown-util';
import DynamoDBP99Latency from './DynamoDBP99Latency';
import DynamoDBP99ResponseSize from './DynamoDBP99ResponseSize';
import DynamoDBP99RowCount from './DynamoDBP99RowCount';
import DynamoDBQPSPerTable from './DynamoDBQPSPerTable';
import { Card, ColumnLayout, FlexGroup, FlexItem } from '@lithos';
import { PipelineNodeTypes } from '../../../service/Pipeline';
import { SelectPopover } from '@lithos';

interface OnlineStoreLatencyProps {
  relatedFvs: FeatureViewFCO[];
  monitoringDateRange: MonitoringDateRange;
}

interface OnlineLatencyGraphOption {
  label: string;
  data: { value: number };
}

export const getRealTimeFeatureViewLabel = (fv: FeatureViewFCO) => {
  const underlyingFeatureView = fv[FeatureViewFCOFields.PIPELINE]?.allLeafNodes?.find((node) => {
    return node?.nodeType === PipelineNodeTypes.FEATURE_VIEW;
  });

  return underlyingFeatureView?.node?.input_name ?? 'Unknown';
};

export const getMetricLabel = (
  result: any,
  relatedFvs: FeatureViewFCO[],
  label?: string,
  isAllFeatures?: FeatureViewFCO[]
) => {
  if (isAllFeatures ?? false) {
    const foundFV = relatedFvs?.find((fv) => {
      const rowLabel = result?.metric.table ?? '';
      if (fv[FeatureViewFCOFields.FEATURE_VIEW_TYPE] === FeatureViewFCOType.REALTIME) {
        const underlyingFeatureView = fv[FeatureViewFCOFields.PIPELINE]?.allLeafNodes?.find((node) => {
          return node?.nodeType === PipelineNodeTypes.FEATURE_VIEW;
        });
        return !!underlyingFeatureView;
      }
      return rowLabel?.indexOf(fv[FCOFields.ID]) > -1;
    });

    if (foundFV && foundFV[FeatureViewFCOFields.FEATURE_VIEW_TYPE] === FeatureViewFCOType.REALTIME) {
      return getRealTimeFeatureViewLabel(foundFV);
    }

    const labelText = `${label}: ${foundFV?.[FCOFields.NAME]}`;
    return labelText;
  }
  return `${label}:  ${result.metric.table}`;
};

const FeatureViewMultiselect: FC<{
  options: (FeatureViewFCO | 'all')[];
  selectedOptions: (FeatureViewFCO | 'all')[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<(FeatureViewFCO | 'all')[]>>;
}> = ({ options, selectedOptions, setSelectedOptions }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  return (
    <FlexItem grow={0} shrink={0}>
      <SelectPopover
        label={selectedOptions[0] === 'all' ? 'All Feature Views' : `Feature Views (${selectedOptions.length})`}
        isOpen={isPopoverOpen}
        setIsOpen={setIsPopoverOpen}
        groups={[
          {
            items: options.map((option) => {
              return {
                name: option === 'all' ? 'All Feature Views' : option[FCOFields.NAME],
                icon: 'FeatureView',
                selectable: true,
                selected: selectedOptions.includes(option),
                onClick: () => {
                  if (option === 'all') {
                    setSelectedOptions(['all']);
                    return;
                  }

                  let newOptions: (FeatureViewFCO | 'all')[];

                  if (selectedOptions.includes(option)) {
                    newOptions = selectedOptions
                      .filter((iteratedOption) => iteratedOption !== option)
                      .filter((option) => option !== 'all');
                  } else {
                    newOptions = [...selectedOptions, option].filter((option) => option !== 'all');
                  }

                  if (newOptions.length === 0) {
                    setSelectedOptions(['all']);
                  } else {
                    setSelectedOptions(newOptions);
                  }
                },
              };
            }),
          },
        ]}
        iconRight="ChevronDown"
        iconLeft="FeatureView"
      />
    </FlexItem>
  );
};

const OnlineStoreLatencySelect: FC<{
  options: OnlineLatencyGraphOption[];
  selectedOption: number;
  setSelectedOption: React.Dispatch<React.SetStateAction<number>>;
}> = ({ options, selectedOption, setSelectedOption }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  return (
    <FlexItem grow={0} shrink={0}>
      <SelectPopover
        label={options.find((iteratedOption) => iteratedOption.data.value === selectedOption)?.label ?? 'Latency'}
        isOpen={isPopoverOpen}
        setIsOpen={setIsPopoverOpen}
        iconLeft="Filters"
        iconRight="ChevronDown"
        groups={[
          {
            items: options.map((option) => {
              return {
                name: option.label,
                selectable: true,
                selected: option.data.value === selectedOption,
                onClick: () => {
                  setSelectedOption(option.data.value);
                },
              };
            }),
          },
        ]}
      />
    </FlexItem>
  );
};

const OnlineStoreLatency: FC<OnlineStoreLatencyProps> = ({ relatedFvs, monitoringDateRange }) => {
  const defaultGraphType: OnlineLatencyGraphOption = {
    label: 'p99',
    data: {
      value: 99,
    },
  };

  // All Feature Views is default
  const graphTypeOptions: OnlineLatencyGraphOption[] = [
    ...[defaultGraphType],
    {
      label: 'p95',
      data: {
        value: 95,
      },
    },
    {
      label: 'p50',
      data: {
        value: 50,
      },
    },
  ];

  const [selectedFeatureViews, setSelectedFeatureViews] = useState<(FeatureViewFCO | 'all')[]>(['all']);
  const [selectedGraphType, setSelectedGraphType] = useState<number>(defaultGraphType.data.value);

  return (
    <>
      <FlexGroup direction="column" gapSize="4">
        <FlexItem grow={0} shrink={0}>
          <FlexGroup direction="row" gapSize="4">
            <FeatureViewMultiselect
              options={[
                'all',
                ...relatedFvs.filter(
                  (fv) => fv[FeatureViewFCOFields.FEATURE_VIEW_TYPE] !== FeatureViewFCOType.REALTIME
                ),
              ]}
              selectedOptions={selectedFeatureViews}
              setSelectedOptions={setSelectedFeatureViews}
            />
            <OnlineStoreLatencySelect
              options={graphTypeOptions}
              selectedOption={selectedGraphType}
              setSelectedOption={setSelectedGraphType}
            />
          </FlexGroup>
        </FlexItem>

        <ColumnLayout>
          <Card title="DynamoDB Latency">
            <DynamoDBP99Latency
              featureViews={
                selectedFeatureViews[0] === 'all'
                  ? relatedFvs
                  : selectedFeatureViews.filter((fv): fv is FeatureViewFCO => fv !== 'all')
              }
              graphType={selectedGraphType}
              monitoringDateRange={monitoringDateRange}
            />
          </Card>

          <Card title="DynamoDB response size">
            <DynamoDBP99ResponseSize
              featureViews={
                selectedFeatureViews[0] === 'all'
                  ? relatedFvs
                  : selectedFeatureViews.filter((fv): fv is FeatureViewFCO => fv !== 'all')
              }
              graphType={selectedGraphType}
              monitoringDateRange={monitoringDateRange}
            />
          </Card>

          <Card title="DynamoDB QPS Per Feature View">
            <DynamoDBQPSPerTable
              featureViews={
                selectedFeatureViews[0] === 'all'
                  ? relatedFvs
                  : selectedFeatureViews.filter((fv): fv is FeatureViewFCO => fv !== 'all')
              }
              graphType={selectedGraphType}
              monitoringDateRange={monitoringDateRange}
            />
          </Card>
          <Card title="DynamoDB Row Count">
            <DynamoDBP99RowCount
              featureViews={selectedFeatureViews[0] === 'all' ? relatedFvs : [selectedFeatureViews[0]]}
              graphType={selectedGraphType}
              monitoringDateRange={monitoringDateRange}
            />
          </Card>
        </ColumnLayout>
      </FlexGroup>
    </>
  );
};

export default OnlineStoreLatency;
